import {cn} from 'util/cn'

import {css, MEDIA_QUERIES} from 'design'
import {useTheme} from 'design/use_theme'
import {useThemeManager} from 'design/use_theme_manager'
import {motion} from 'framer-motion'
import {Button} from 'primitives/button'
import {Stack} from 'primitives/containers'
import React, {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,} from 'react'
import {createPortal} from 'react-dom'
import {useDispatch, useSelector} from 'react-redux'

import {useMemberCallManager} from '../../components/calls/call_manager/member_call_manager_hook'
import {ActiveCallBanner} from '../../components/calls/controls/active_call_banner'
import {useCallManager} from '../../contexts/call_manager_context'
import useNotificationManager from '../../controls/notification_manager'
import usePreviousProps from '../../hooks/previous_props_hook'
import {useMediaQuery} from '../../hooks/use_media_query'
import {usePersistReduxStores} from '../../hooks/use_persist_redux_stores'
import {usePersistedState} from '../../hooks/use_persisted_state'
import {setDestroyed} from '../../reduxes/slices/api/actions'
import {getApiQuestions} from '../../reduxes/slices/api/selectors'
import {getCurrentMember, getCurrentMemberId,} from '../../reduxes/slices/current/selectors'
import {getSelectedWelcomeMessageId, getSelectedWidgetQuestionId,} from '../../reduxes/slices/selected/selectors'
import {getWelcomeMessages} from '../../reduxes/slices/topics/selectors'
import {getSelectedWelcomeMessage, makeGetWelcomeMessageById,} from '../../reduxes/slices/welcome_messages/selectors'
import {useSelectorDelegateStore} from '../../reduxes/stores/root_store/hooks'
import {AppLocationContext, TOPIC_VIEW, WIDGET_VIEW,} from '../admin/app_location_provider'
import {CarouselCollectionControls} from './carousel_collection_controls'
import {CarouselFullscreenControls} from './carousel_fullscreen_controls'
import {
  dialogElement,
  FLOATING_IFRAME_HEIGHT,
  iframeElements,
  isFirstInCollection,
  LAUNCHER_HEIGHT,
  LAUNCHER_WIDTH,
  SPEAKEASY_CONTAINER_ID,
  WIDGET_IFRAME_HEIGHT,
  WIDGET_LAYOUT,
} from './constants'
import {InfiniteScrollPlaceholders} from './infinite_scroll_placeholders'
import {ScrollerCollectionControls} from './scroller_collection_controls'
import {useCollectionsScrollManagement} from './use_collections_scroll_management'
import {useWidgetLayout} from './use_widget_layout'
import {WidgetLauncher} from './widget_launcher'
import {VIDEO_RATIO, WidgetPageRouter} from './widget_page_router'
import ahoy from 'ahoy.js'

// eslint-disable-next-line
// @ts-ignore
const { type, fullscreen, collection } = window.speakeasySettings

type Props = {
  rootStore: any
  subStore: any
}

export const Member = ({ rootStore, subStore }: Props) => {
  const resizeObserver = useRef<ResizeObserver>()
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = useState()
  const desktop = useMediaQuery(MEDIA_QUERIES.md, window.parent)
  const [welcomeVideoPlayed, setWelcomeVideoPlayed] = usePersistedState(
    'widget_welcome_video_played',
    false,
  )
  const currentMember = useSelectorDelegateStore(getCurrentMember)
  const welcomeMessage = useSelector(getSelectedWelcomeMessage)

  const { appLocation, prevAppLocation } = useContext(AppLocationContext)
  usePersistReduxStores(rootStore, [subStore])

  useNotificationManager()
  useMemberCallManager(currentMember?.id)
  useThemeManager()
  const [, setTheme] = useTheme()
  const {
    activeCall: { isOnCall, navToVideoView },
  } = useCallManager()
  const prevIsOnCall = usePreviousProps(isOnCall)
  const showActiveCallBanner =
    isOnCall && appLocation.topicView === TOPIC_VIEW.CHAT_VIEW

  const mediumBp = useMediaQuery(MEDIA_QUERIES.md)
  const currentMemberId = useSelector(getCurrentMemberId)
  const questions = useSelector(getApiQuestions)
  const dispatch = useDispatch()
  const selectedWelcomeMessageId = useSelector(getSelectedWelcomeMessageId)
  const selectedWidgetQuestionId = useSelector(getSelectedWidgetQuestionId)
  const { setAppLocation } = useContext(AppLocationContext)
  const welcomeMessages: any = useSelector(getWelcomeMessages)
  const [noOfPostersLoaded, setNoOfPostersLoaded] = useState(0)

  const rootWelcomeMessageId = useSelectorDelegateStore(
    (state: any) => state.initialAppLocation.welcomeMessageId,
  )
  const getWelcomeMessageById = useMemo(makeGetWelcomeMessageById, [])
  const rootWelcomeMessage = useSelector((state) =>
    // eslint-disable-next-line
    // @ts-ignore
    getWelcomeMessageById(state, rootWelcomeMessageId),
  )
  const [collectionReady, setCollectionReady] = useState(false)

  useEffect(() => {
    if (collection) {
      const onCollectionReady = () => {
        setCollectionReady(true)
      }
      window.addEventListener(COLLECTION_READY_EVENT, onCollectionReady)
      window.widgetReady = true

      const count = iframeElements.reduce((acc, iframe) => {
        return iframe.contentWindow.widgetReady ? acc + 1 : acc
      }, 0)
      if (count === iframeElements.length) {
        iframeElements.forEach((iframe) => {
          iframe.contentWindow.dispatchEvent(new Event(COLLECTION_READY_EVENT))
        })
      }

      return () => {
        window.removeEventListener(COLLECTION_READY_EVENT, onCollectionReady)
      }
    }
  }, [])

  const {
    widgetLayout,
    setWidgetLayout,
    toggleLayout,
    isSelectedInCollection,
    positionInCollection,
    collectionFullscreen,
    collectionMuted,
    prevCarouselScrollLeft,
  } = useWidgetLayout({ collectionReady })

  const prevPositionInCollection = usePreviousProps(positionInCollection)
  const prevWidgetLayout = usePreviousProps(widgetLayout)

  const dialogIsVerticalScrollContainer = useMemo(() => {
    if (!collection) {
      return false
    }
    if (collectionFullscreen) {
      return !desktop
    } else if (
      (type === 'embedded' && widgetLayout === WIDGET_LAYOUT.EMBEDDED) || (type === 'contact_form' && widgetLayout === WIDGET_LAYOUT.CONTACT_FORM)
    ) {
      return true
    }
    return false
  }, [collectionFullscreen, desktop, widgetLayout])

  const { isCollectionIntersecting } = useCollectionsScrollManagement({
    dialogIsVerticalScrollContainer,
    positionInCollection,
    widgetLayout,
    collectionReady,
  })

  useLayoutEffect(() => {
    const frameElement = window.frameElement as HTMLElement
    frameElement.removeAttribute('style')
    dialogElement.removeAttribute('style')
  }, [])

  useLayoutEffect(() => {
    const dialog = dialogElement
    dialog.style.background = 'none'
    dialog.style.border = 'none'
    dialog.style.padding = '0'
    dialog.style.position = 'relative'
    dialog.style.overflow = 'hidden'

    // purpose of dialogChildContainer is to workaround the browser forcing the dialog element to have a margin
    const dialogChildContainer = dialogElement.firstChild as HTMLDivElement
    dialogChildContainer.style.position = 'relative'

    if (collection) {
      if (
        type === 'carousel' ||
        type === 'embedded' ||
        type === 'contact_form'
      ) {
        dialog.style.width = '100%'
      }

      if (type === 'carousel') {
        dialog.style.display = 'flex'
        dialog.style.justifyContent = 'center'
        dialogChildContainer.style.overflow = 'auto hidden'
      }

      if (dialogIsVerticalScrollContainer) {
        dialogChildContainer.style.display = 'block'
        dialogChildContainer.style.overflow = 'hidden auto'
        dialogChildContainer.style.scrollSnapType = 'y mandatory'
        dialogChildContainer.style.overscrollBehavior = 'none'
      } else {
        dialogChildContainer.style.display = 'flex'
      }
    }

    switch (widgetLayout) {
      case WIDGET_LAYOUT.EMBEDDED:
        dialog.style.height = '100%'
        break
      case WIDGET_LAYOUT.CONTACT_FORM:
        dialog.style.width = '100%'
        dialog.style.height = '100%'
        break
      case WIDGET_LAYOUT.FULLSCREEN:
        dialog.style.position = 'fixed'
        dialog.style.width = '100%'
        dialog.style.height = '100%'

        dialogChildContainer.style.position = 'fixed'
        dialogChildContainer.style.inset = '0'
        if (!collection) {
          dialogChildContainer.style.display = 'flex'
          dialogChildContainer.style.justifyContent = 'center'
          dialogChildContainer.style.alignItems = 'center'
        }
        break
    }

    return () => {
      dialog.removeAttribute('style')
    }
  }, [desktop, dialogIsVerticalScrollContainer, widgetLayout])

  const setIframeDynamicStyles = useCallback(
    (cHeight) => {
      const frameElement = window.frameElement as HTMLElement
      let height, width

      switch (widgetLayout) {
        case WIDGET_LAYOUT.EMBEDDED:
          width = `${(cHeight * VIDEO_RATIO.width) / VIDEO_RATIO.height + 6}px`
          break
        case WIDGET_LAYOUT.FLOATING:
        case WIDGET_LAYOUT.FLOATING_CLOSED:
          width = `${(cHeight * VIDEO_RATIO.width) / VIDEO_RATIO.height + 60}px`
          break
        case WIDGET_LAYOUT.WIDGET:
          width = `${(cHeight * VIDEO_RATIO.width) / VIDEO_RATIO.height + 60}px`
          break
        case WIDGET_LAYOUT.FULLSCREEN:
          width = `${(cHeight * VIDEO_RATIO.width) / VIDEO_RATIO.height + 6}px`
      }

      height
        ? frameElement.style?.setProperty('--height', height)
        : frameElement.style?.removeProperty('--height')
      width
        ? frameElement.style?.setProperty('--width', width)
        : frameElement.style?.removeProperty('--width')
    },
    [widgetLayout],
  )

  useLayoutEffect(() => {
    const frameElement = window.frameElement as HTMLElement
    frameElement.style.border = 'none'
    frameElement.style.userSelect = 'none'

    if (collection) {
      frameElement.style.pointerEvents = 'auto'
    }

    setIframeDynamicStyles(containerHeight)

    switch (widgetLayout) {
      case WIDGET_LAYOUT.WIDGET_MINIMISED:
        frameElement.style.position = 'fixed'
        frameElement.style.inset = 'auto 0px 0px auto'
        frameElement.style.zIndex = '2147483000'
        frameElement.style.width = `${LAUNCHER_WIDTH}px`
        frameElement.style.height = `${LAUNCHER_HEIGHT}px`
        break
      case WIDGET_LAYOUT.FLOATING:
        frameElement.style.position = 'fixed'
        frameElement.style.zIndex = '2147483000'
        frameElement.style.width = 'var(--width)'
        frameElement.style.height = '100%'
        frameElement.style.inset = 'auto 0px 0px auto'
        frameElement.style.maxHeight = `min(100%, ${FLOATING_IFRAME_HEIGHT}px)`
        break
      case WIDGET_LAYOUT.EMBEDDED:
        frameElement.style.height = '100%'
        frameElement.style.width = 'var(--width)'
        frameElement.style.inset = 'auto 0px 0px auto'
        break
      case WIDGET_LAYOUT.WIDGET:
        frameElement.style.position = 'fixed'
        frameElement.style.inset = 'auto 0px 0px auto'
        frameElement.style.zIndex = '2147483000'
        frameElement.style.width = 'var(--width)'
        frameElement.style.height = '100%'
        frameElement.style.maxHeight = `min(100%, ${WIDGET_IFRAME_HEIGHT}px)`
        break
      case WIDGET_LAYOUT.FLOATING_CLOSED:
        frameElement.style.position = 'fixed'
        frameElement.style.inset = 'auto -138px -250px auto'
        frameElement.style.zIndex = '2147483000'
        frameElement.style.width = 'var(--width)'
        frameElement.style.height = '100%'
        frameElement.style.maxHeight = `min(100%, ${FLOATING_IFRAME_HEIGHT}px)`
        break
      case WIDGET_LAYOUT.WIDGET_MAXIMISED:
        frameElement.style.width = '100%'
        frameElement.style.height = '100%'
        break
      case WIDGET_LAYOUT.CONTACT_FORM:
        frameElement.style.width = '100%'
        frameElement.style.height = '100%'
        break
      case WIDGET_LAYOUT.FULLSCREEN:
        if (desktop) {
          frameElement.style.position = 'relative'
          frameElement.style.width = 'var(--width)'
          frameElement.style.height = '100%'
        } else {
          frameElement.style.width = '100%'
          frameElement.style.height = '100%'
        }
        break
    }

    return () => {
      frameElement.removeAttribute('style')
    }
  }, [containerHeight, desktop, setIframeDynamicStyles, widgetLayout])

  useLayoutEffect(() => {
    if (
      collection &&
      (type === 'floating' || type === 'widget') &&
      !isFirstInCollection
    ) {
      const frameElement = window.frameElement as HTMLElement
      frameElement.hidden = widgetLayout !== WIDGET_LAYOUT.FULLSCREEN
    }
  }, [widgetLayout])

  useLayoutEffect(() => {
    const widgetContainer = window.frameElement.closest(
      '[data-widget]',
    ) as HTMLDivElement
    widgetContainer.style.display = 'flex'
    widgetContainer.style.alignItems = 'center'
    widgetContainer.style.height = '100%'

    if (collection) {
      widgetContainer.style.pointerEvents = 'none'
    }

    switch (widgetLayout) {
      case WIDGET_LAYOUT.FULLSCREEN:
        if (fullscreen) {
          window.frameElement.hidden = false
        }
        if (desktop) {
          widgetContainer.style.position = 'fixed'
        } else {
          widgetContainer.style.width = '100%'
        }
        if (collection) {
          if (
            desktop &&
            (type === 'carousel' || widgetLayout === WIDGET_LAYOUT.FULLSCREEN)
          ) {
            widgetContainer.style.height = 'calc(100% - 40px)'
            widgetContainer.style.top = '50%'
            widgetContainer.style.left = '50%'
            setFullscreenTranslates(positionInCollection)
            widgetContainer.style.translate = 'var(--xTranslate) -50%'
            widgetContainer.style.scale =
              positionInCollection === 0 ? '1' : SCALE.toString()
            if (prevWidgetLayout === WIDGET_LAYOUT.FULLSCREEN) {
              widgetContainer.style.transition =
                'translate 0.2s ease, scale 0.2s ease'
            }
          }
        } else {
          if (desktop) {
            widgetContainer.style.height = 'calc(100% - 40px)'
          }
        }
        break
      case WIDGET_LAYOUT.WIDGET_MAXIMISED:
        widgetContainer.style.position = 'fixed'
        widgetContainer.style.inset = '0'
        break
    }

    if (dialogIsVerticalScrollContainer) {
      widgetContainer.style.scrollSnapAlign = 'start'
      widgetContainer.style.scrollSnapStop = 'always'
    }

    return () => {
      widgetContainer.removeAttribute('style')
    }
  }, [
    positionInCollection,
    widgetLayout,
    desktop,
    prevWidgetLayout,
    prevPositionInCollection,
    dialogIsVerticalScrollContainer,
  ]) // do not add prevWidgetLayout or prevPositionInCollection to deps

  useLayoutEffect(() => {
    // eslint-disable-next-line
    // @ts-ignore
    const theme = window.speakeasySettings.theme || 'light'
    if (theme === 'light' || theme === 'dark')
      setTheme({
        currentTheme: theme,
        overrideSystemOnMediaQueryChange: true,
      })
  }, [setTheme])

  useEffect(() => {
    if (rootWelcomeMessage) {
      const welcomePosterImg = new Image()
      const previewPosterImg = new Image()

      welcomePosterImg.src = rootWelcomeMessage.welcome_video_poster
      previewPosterImg.src = rootWelcomeMessage.preview_video_poster

      const onLoad = () => {
        setNoOfPostersLoaded((prevState) => prevState + 1)
      }
      welcomePosterImg.addEventListener('load', onLoad)
      previewPosterImg.addEventListener('load', onLoad)

      return () => {
        welcomePosterImg.removeEventListener('load', onLoad)
        previewPosterImg.removeEventListener('load', onLoad)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootWelcomeMessage])

  useLayoutEffect(() => {
    if (widgetLayout !== WIDGET_LAYOUT.WIDGET_MINIMISED) {
      const container = containerRef.current
      if (container) {
        const onResize = () => {
          setIframeDynamicStyles(container.clientHeight)
          setContainerHeight(container.clientHeight)
        }
        const ro = new ResizeObserver(onResize)
        ro.observe(container)
        resizeObserver.current = ro

        return () => {
          ro.disconnect()
          resizeObserver.current = undefined
        }
      }
    }
  }, [setIframeDynamicStyles, widgetLayout])

  // style the container for the error boundary
  useLayoutEffect(() => {
    return () => {
      const frameElement = window.frameElement as HTMLElement
      frameElement.style.width = '195px'
      frameElement.style.height = '135px'
      frameElement.style.inset = 'auto 10px 20px auto'
    }
  }, [])

  // If the user triggers a call on mobile, navigate directly to the Call view
  // This is specifically to reduce the jarring effect of starting a call on mobile
  useEffect(() => {
    if (mediumBp) {
      return
    }

    if (prevIsOnCall === false && isOnCall && navToVideoView) {
      navToVideoView()
    }
  }, [prevIsOnCall, isOnCall, navToVideoView, mediumBp])

  useEffect(() => {
    if (collection) {
      ahoy.track("Collection Played", {account: welcomeMessage.account_sgid, collection: window.speakeasySettings?.collection})
    }
  }, [])

  useEffect(() => {
    if (
      prevAppLocation.topicView === TOPIC_VIEW.VIDEO_VIEW &&
      appLocation.topicView !== TOPIC_VIEW.VIDEO_VIEW
    ) {
      switch (type) {
        case 'embedded':
          setWidgetLayout(WIDGET_LAYOUT.EMBEDDED)
          break
        case 'widget':
          setWidgetLayout(WIDGET_LAYOUT.WIDGET)
          break
      }
    }
  }, [appLocation.topicView, prevAppLocation.topicView, setWidgetLayout])

  // preload welcome message posters
  useEffect(() => {
    welcomeMessages.forEach((wm: any) => {
      const welcomePosterImg = new Image()
      welcomePosterImg.src = wm.welcome_video_poster
    })
  }, [welcomeMessages])

  // handle questions getting deleted
  useEffect(() => {
    const questionIds = Object.keys(
      window.preloadData.subStores[currentMemberId || -1]?.api?.questions
        ?.data || {},
    )
    Object.keys(questions).forEach((questionId) => {
      if (!questionIds.includes(questionId)) {
        const q = questions[questionId]
        if (
          questionId === selectedWidgetQuestionId ||
          (selectedWidgetQuestionId === 'root' &&
            q.root &&
            q.welcome_message_id === selectedWelcomeMessageId)
        ) {
          setAppLocation({
            widgetView: null,
            widgetQuestionId: 'root',
            productURL: null,
          })
        }
        setTimeout(() => {
          dispatch(setDestroyed('questions', questionId))
        }, 1000)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions])

  useEffect(() => {
    if (
      selectedWidgetQuestionId &&
      selectedWidgetQuestionId !== 'root' &&
      !questions[selectedWidgetQuestionId]
    ) {
      setAppLocation({
        widgetView: null,
        widgetQuestionId: 'root',
        productURL: null,
      })
    }
  }, [questions, selectedWidgetQuestionId, setAppLocation])

  const closeWidget = () => {
    setWidgetLayout(WIDGET_LAYOUT.FLOATING_CLOSED)
  }

  useLayoutEffect(() => {
    if (collectionFullscreen && desktop) {
      const onResize = () => {
        setFullscreenTranslates(positionInCollection)
      }
      window.parent.addEventListener('resize', onResize)

      return () => {
        window.parent.removeEventListener('resize', onResize)
      }
    }
  }, [collectionFullscreen, desktop, positionInCollection])

  const showBorderAndBackground =
    widgetLayout !== WIDGET_LAYOUT.FLOATING &&
    (appLocation.widgetView === WIDGET_VIEW.PAST_CONVERSATIONS ||
      appLocation.widgetView === WIDGET_VIEW.LANDING_MESSENGER ||
      appLocation.widgetView === WIDGET_VIEW.MESSENGER ||
      (appLocation.widgetView === WIDGET_VIEW.PRODUCTS &&
        !!appLocation.productURL))

  return (
    <Stack className={cn(appContainerStyles({ widgetLayout }), 'topic-detail')}>
      {widgetLayout !== WIDGET_LAYOUT.WIDGET_MINIMISED && (
        <>
          <motion.div
            id={SPEAKEASY_CONTAINER_ID}
            ref={containerRef}
            initial={type === 'widget' ? { y: 30, opacity: 0 } : {}}
            animate={
              type === 'widget' ? { y: 0, opacity: 1, z: '0px' } : { z: '0px' }
            } // z: fixes safari overflow none with border radius
            transition={{ type: 'spring', bounce: 0.1 }}
            className={containerStyles({
              type,
              widgetLayout,
              desktop,
              blank: !welcomeMessage?.welcome_video,
              showBorder:
                showBorderAndBackground &&
                (!collection ||
                  (widgetLayout === WIDGET_LAYOUT.FULLSCREEN &&
                    positionInCollection === 0)),
              showBackground: collection
                ? showBorderAndBackground &&
                  (positionInCollection === 0 ||
                    widgetLayout !== WIDGET_LAYOUT.FULLSCREEN)
                : showBorderAndBackground,
            })}
          >
            <>
              {showActiveCallBanner && (
                <ActiveCallBanner className={activeCallBannerStyles()} />
              )}
              <WidgetPageRouter
                welcomeVideoPlayed={welcomeVideoPlayed}
                setWelcomeVideoPlayed={setWelcomeVideoPlayed}
                widgetLayout={widgetLayout}
                setWidgetLayout={setWidgetLayout}
                toggleLayout={toggleLayout}
                isSelectedInCollection={isSelectedInCollection}
                positionInCollection={positionInCollection}
                collectionReady={collectionReady}
                collectionMuted={collectionMuted}
                prevCarouselScrollLeft={prevCarouselScrollLeft}
                isCollectionIntersecting={isCollectionIntersecting}
              />
            </>
          </motion.div>
          {widgetLayout === WIDGET_LAYOUT.FULLSCREEN &&
            (!collection || isSelectedInCollection) && (
              <>
                {createPortal(
                  <motion.div
                    style={{
                      position: 'fixed',
                      inset: 0,
                      ...(!collection && { background: 'rgba(1,1,1,0.7)' }),
                      zIndex: -1,
                      display: 'block',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.1 }}
                    onClick={() => {
                      !fullscreen && toggleLayout(WIDGET_LAYOUT.FULLSCREEN)
                    }}
                  />,
                  dialogElement.firstChild,
                )}
                {collection && desktop && <CarouselFullscreenControls />}
              </>
            )}
        </>
      )}
      {(widgetLayout === WIDGET_LAYOUT.WIDGET_MINIMISED ||
        (type === 'widget' &&
          desktop &&
          widgetLayout !== WIDGET_LAYOUT.FULLSCREEN)) && (
        <WidgetLauncher
          rootWelcomeMessage={rootWelcomeMessage}
          noOfPostersLoaded={noOfPostersLoaded}
          isOpen={widgetLayout !== WIDGET_LAYOUT.WIDGET_MINIMISED}
          toggleLayout={toggleLayout}
        />
      )}
      {widgetLayout === WIDGET_LAYOUT.FLOATING && (
        <Button
          id={'closeWidgetBtn'}
          className={closeButtonStyles()}
          shape="circle"
          size={'small'}
          onClick={closeWidget}
        >
          <i className="fa fa-times" />
        </Button>
      )}
      {collection &&
        isFirstInCollection &&
        widgetLayout === WIDGET_LAYOUT.FULLSCREEN &&
        createPortal(
          <motion.div
            style={{
              position: 'fixed',
              inset: 0,
              background: fullscreen ? 'black' : 'rgba(1,1,1,0.7)',
              zIndex: -2,
              display: 'block',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.1 }}
          />,
          // eslint-disable-next-line
          // @ts-ignore
          dialogElement.firstChild,
        )}
      {collection && type === 'carousel' && <CarouselCollectionControls />}
      {collection &&
        (type === 'embedded' || type === 'contact_form') &&
        desktop &&
        widgetLayout !== WIDGET_LAYOUT.FULLSCREEN && (
          <ScrollerCollectionControls />
        )}
      <InfiniteScrollPlaceholders
        dialogIsVerticalScrollContainer={dialogIsVerticalScrollContainer}
        welcomeMessage={welcomeMessage}
        widgetLayout={widgetLayout}
      />
    </Stack>
  )
}

const COLLECTION_READY_EVENT = 'COLLECTION_READY'

const appContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  height: '100%',
  overflow: 'hidden',

  '&:hover': {
    '#closeWidgetBtn': {
      visibility: 'visible',
    },
  },

  variants: {
    widgetLayout: {
      [WIDGET_LAYOUT.FLOATING]: {
        transition: 'transform 0.3s ease, transform-origin 0.3s ease',

        '&:hover': {
          transform: 'scale(1.05) !important',
        },
      },
      [WIDGET_LAYOUT.FLOATING_CLOSED]: {
        transition: 'transform 0.3s ease, transform-origin 0.3s ease',

        '&:hover': {
          transform: 'scale(1.05) !important',
        },
      },
      [WIDGET_LAYOUT.EMBEDDED]: {},
      [WIDGET_LAYOUT.WIDGET]: {},
      [WIDGET_LAYOUT.WIDGET_MINIMISED]: {},
      [WIDGET_LAYOUT.WIDGET_MINIMISED]: {},
      [WIDGET_LAYOUT.FULLSCREEN]: {},
      [WIDGET_LAYOUT.CONTACT_FORM]: {},
    },
  },
})

// NOTE: when changing space outside the content-box (e.g. margin and border) update the height of this container
// NOTE: also update the iframe width and height to match (inside setIframeDynamicStyles function above)
const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: 'calc(100% - 6px)', // minus margin
  border: '3px solid transparent', // if border-width changed, update height
  boxSizing: 'content-box',
  overflow: 'hidden',
  position: 'relative',
  aspectRatio: '9 / 16',
  borderRadius: '20px',
  backgroundClip: 'padding-box',

  variants: {
    type: {
      embedded: {},
      floating: {},
      widget: {
        margin: '30px 30px 0 30px', // if changed, update height
      },
      contact_form: {
        backgroundSize: 'cover',
      },
    },
    widgetLayout: {
      [WIDGET_LAYOUT.FLOATING]: {
        height: 'calc(100% - 60px)', // minus margin
        margin: '30px', // if changed, update height
        border: 'none',
        boxShadow: 'rgb(0,0,0,0.7) 0px 1px 10px',
      },
      [WIDGET_LAYOUT.FLOATING_CLOSED]: {
        height: 'calc(100% - 60px)', // minus margin
        margin: '30px', // if changed, update height
        border: 'none',
        boxShadow: 'rgb(0,0,0,0.7) 0px 1px 10px',
      },
      [WIDGET_LAYOUT.FULLSCREEN]: {
        height: 'calc(100% - 6px)', // minus margin
        margin: 0,
      },
      [WIDGET_LAYOUT.WIDGET]: {
        height: 'calc(100% - 60px)', // minus margin
        border: 'none', // if changed, update height
        boxShadow: 'rgb(0,0,0,0.7) 0px 1px 10px',
      },
      [WIDGET_LAYOUT.WIDGET_MAXIMISED]: {
        boxShadow: 'rgb(0,0,0,0.7) 0px 1px 10px',
        aspectRatio: 'unset',
        width: '100%',
      },
      [WIDGET_LAYOUT.WIDGET_MINIMISED]: {},
      [WIDGET_LAYOUT.CONTACT_FORM]: {
        width: 'calc(100% - 6px)', // minus border-width
      },
      [WIDGET_LAYOUT.EMBEDDED]: {},
    },
    desktop: {
      true: {},
      false: {},
    },
    blank: {
      true: {
        backgroundColor: '$backgroundSecondary',
      },
    },
    showBorder: {
      true: {
        borderColor: '$grey100',
      },
    },
    showBackground: {
      true: {
        backgroundColor: '$backgroundSecondary',
      },
    },
  },

  compoundVariants: [
    {
      widgetLayout: WIDGET_LAYOUT.FULLSCREEN,
      desktop: false,
      css: {
        borderRadius: 0,
        margin: 0,
        width: '100%',
        height: '100%',
        border: 'none',
      },
    },
  ],
})

const activeCallBannerStyles = css({
  height: 'unset !important',
  padding: '$2 0',
  zIndex: 1,
  pointerEvents: 'auto !important',
})

const closeButtonStyles = css({
  position: 'absolute !important',
  top: '22px',
  right: '22px',
  visibility: 'hidden',

  '&:hover': {
    background: '$grey200',
  },
})

const SCALE = 0.8

const setFullscreenTranslates = (positionInCollection) => {
  const selectedWidgetWidth = ((window.parent.innerHeight - 40) * 9) / 16
  const otherWidgetsWidth = selectedWidgetWidth * SCALE
  const gap = 120

  let xTranslate
  if (positionInCollection < 0) {
    xTranslate = `calc(-50% - ${selectedWidgetWidth / 2}px - ${
      otherWidgetsWidth / 2 +
      gap +
      (otherWidgetsWidth + gap) * (Math.abs(positionInCollection) - 1)
    }px)`
  } else if (positionInCollection > 0) {
    xTranslate = `calc(-50% + ${selectedWidgetWidth / 2}px + ${
      otherWidgetsWidth / 2 +
      gap +
      (otherWidgetsWidth + gap) * (Math.abs(positionInCollection) - 1)
    }px)`
  } else {
    xTranslate = '-50%'
  }

  const widgetContainer = window.frameElement.closest(
    '[data-widget]',
  ) as HTMLDivElement
  widgetContainer.style?.setProperty('--xTranslate', xTranslate)
}
